import { CodingFieldsService } from '../../services/coding-fields.service';
import { CodingField, SelectableCodingField } from 'src/app/models/field/coding-field.model';
import { Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';

@Injectable({
	'providedIn': 'root'
})
export class CustomCodingFieldsManager {

	public all$: Observable<Map<string, CodingField>>;
	private allSubject = new ReplaySubject<Map<string, CodingField>>(1);

	/** All fields, parent or child, by field ID. */
	private all = new Map<string, CodingField>();

	/** Custom coding sections for the current case (i.e. the parent custom coding fields) and whether or not they're selected (shown). */
	private sections = new Map<string, SelectableCodingField>();

	/**
	 * These are all of the custom coding fields for the current case by parent field ID.
	 * Values array includes parents.
	 */
	private childrenByParentId = new Map<string, CodingField[]>();

	private parentByChildId = new Map<string, CodingField>();

	private fieldShowPreferences = new Map<string, boolean>();

	constructor(private codingFieldsService: CodingFieldsService) {
		this.all$ = this.allSubject.asObservable();
	}

	/**
	 * Retrieves custom coding fields created for the current case from the server.
	 * Must be called to initialize the class with custom coding data.
	 */
	retrieve(respectLayout?: boolean) {
		this.codingFieldsService.getFields(respectLayout).subscribe(data => {
			if (data) data.value.forEach(cf => {
				this.setChildByParent(cf);

				if (cf.parentFieldId === cf.fieldId) {
					this.sections.set(cf.fieldId, new SelectableCodingField(this.fieldShowPreferences.get(cf.fieldId), cf));
				}

				this.all.set(cf.fieldId, new CodingField(cf));
			});

			this.syncParentsWithChildId();
			this.allSubject.next(this.all);
		});
	}

	setChildByParent(field: CodingField) {
		if (this.childrenByParentId.has(field.parentFieldId)) {
			const children = this.childrenByParentId.get(field.parentFieldId);
			const childIndex = children.findIndex(c => c.fieldId === field.fieldId);

			if (childIndex === -1)
				this.childrenByParentId.get(field.parentFieldId).push(new CodingField(field));
		} else {
			this.childrenByParentId.set(field.parentFieldId, [new CodingField(field)]);
		}
	}

	syncParentsWithChildId() {
		this.childrenByParentId.forEach((children: CodingField[], parentId: string) => {
			children.forEach(child => {
				this.parentByChildId.set(child.fieldId, this.sections.get(parentId)); // this needs to make sure that field does not exist
			});
		});
	}

	/**
	 * Retrieves all parent coding fields (sections) sorted by rank.
	 */
	getParents(): CodingField[] {
		return Array.from(this.sections.values()).sort((a, b) => a.rank > b.rank ? 1 : -1);
	}

	/**
	 * Retrieves parent coding fields (sections) that have been selected by the select fields dialog.
	 */
	getSelectedParents(): CodingField[] {
		return Array.from(this.sections.values()).filter(s => s.selected);
	}

	/**
	 * Retrieves coding fields that are children of the specified parent coding field (section) sorted by rank.
	 */
	getChildren(parentId: string): CodingField[] {
		return this.childrenByParentId.get(parentId).sort((a, b) => a.rank > b.rank ? 1 : -1);
	}

	getChildMapSorted(): Map<string, CodingField[]> {
		const sortedMap = new Map<string, CodingField[]>();
		for (const [parentId, childrenArray] of this.childrenByParentId.entries()) {
			const sortedChildren = [...childrenArray].filter(f => f.fieldId !== f.parentFieldId).sort((a, b) => a.rank > b.rank ? 1 : -1);
			sortedMap.set(parentId, sortedChildren);
		}
		return sortedMap;
	}

	/** Retrieves a reference to all children. */
	getAllChildren(): CodingField[] {
		return [].concat(...Array.from(this.childrenByParentId.values())).filter(f => f.fieldId !== f.parentFieldId);
	}

	/**
	 * Retrieves a flattened array of coding fields sorted by rank.
	 */
	getSelectedParentsAndTheirChildrenSorted(): CodingField[] {
		return [].concat(...this.getSelectedParents().map(s => this.getChildren(s.fieldId))).sort((a: CodingField, b: CodingField) => a.rank > b.rank ? 1 : -1);
	}

	/** Whether or not any parents are selected. */
	areAnySelected(): boolean {
		return this.getSelectedParents().length > 0;
	}

	/**
	 * Marks matching parents as selected or not, depending on the specified selection.
	 * If a match is not found, selection is skipped.
	 */
	sync(fields: SelectableCodingField[]) {
		fields.forEach(f => {
			if (this.sections.has(f.fieldId)) {
				this.sections.get(f.fieldId).selected = f.selected;
			}
		});
	}

	syncBySectionId(sectionIds: Set<string>) {
		this.sections.forEach(section => {
			const selected = sectionIds.has(section.fieldId);
			section.selected = selected;
		});
	}

	/** Retrieves a deep copy of parents as selectable coding fields. */
	getCopyOfParents(): SelectableCodingField[] {
		return JSON.parse(JSON.stringify(this.getParents()));
	}

	/**
	 * Attempts to retrieve the coding field for the specified field ID, parent or child.
	 * Returns undefined if not found.
	 */
	getFieldById(id: string): CodingField {
		return this.all.get(id);
	}

	/** Retrieves the parent coding field for the specified child ID. */
	getParent(childId: string): CodingField {
		return this.parentByChildId.get(childId);
	}
}
